import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useAnalytics } from '@cld/analytics';
import { ApplicationsDropdownItem } from '../../../components/ApplicationsDropdownMenu';
import { useApplicationWrapperContext } from '../../../context/applicationWrapper/ApplicationWrapper.context';
import { useApplicationsDropdownWrapperContext } from '../../../context/applicationDropdownWrapper/ApplicationsDropdownWrapper.context';
import { ApplicationInfo } from '../../../ApplicationWrapper.types';
import { messages } from '../../../i18n';
import { ErrorRefresh } from '../../../components/ErrorRefresh/ErrorRefresh';
import { FetchStatus } from '../../../api/api.types';
import { ApplicationsExplorerLink } from '../ApplicationsExplorerLink/ApplicationsExplorerLink';
import { useCustomerIdContext } from '../../../context';
import { isAnyTypeOfAdmin } from '../../../api/api.utils';
import { BetaBadge } from '../../../components/BetaBadge/BetaBadge';
import { ApplicationSvgIcon } from '../ApplicationSvgIcon/ApplicationSvgIcon';
import { applicationsInfo } from '../../../api/productList/ApplicationsInfoMap.const';
import { EarlyAccessBadge } from '../../../components/EarlyAccessBadge/EarlyAccessBadge';
import { AppId, ApplicationsListCategories, UserRole } from '@cld/application-wrapper-common';
import { useVideoAppAccess } from '../../../useVideoAppAccess';
import { useUserData } from '../../../context';

// will be removed after Explorer App has done
const isExplorerLinkFlag = localStorage.getItem('explorer-link') === 'true';

export interface ApplicationsDropdownItemsProps {
  showVideoAppSelector?: boolean;
}

// istanbul ignore next
export const ApplicationsDropdownItems: FC<ApplicationsDropdownItemsProps> = ({ showVideoAppSelector }) => {
  const { selectedAppId } = useApplicationWrapperContext();
  const { applicationGroupsList, applicationListFetchStatus, onAppSelect } = useApplicationsDropdownWrapperContext();
  const analytics = useAnalytics('products menu');
  const { formatMessage } = useIntl();
  const { userData } = useUserData();
  const { currentCustomer } = useCustomerIdContext();
  const isVideoAppSelectorVisible = useVideoAppAccess(!!showVideoAppSelector);

  const onClick = (item: ApplicationInfo, event: MouseEvent) => {
    analytics(`switch to ${item.name}`, 'switch product');

    if (!item.openInNewWindow) {
      sessionStorage.setItem('selectedApp', item.appId);
      onAppSelect?.(item.appId, event);
    }
  };

  const renderApplicationItem = (item: ApplicationInfo) => (
    <ApplicationsDropdownItem
      key={item.appId}
      href={item.href({ customerExternalId: currentCustomer?.externalId ?? '' })}
      id={item.appId}
      icon={<ApplicationSvgIcon size="sm" svg={item.iconActive} />}
      isSelected={selectedAppId === item?.appId}
      rightSlot={
        <>
          {item.isBeta && <BetaBadge />}
          {item.isEarlyAccess && <EarlyAccessBadge />}
        </>
      }
      name={formatMessage(messages[item.name])}
      description={formatMessage(messages[item.description])}
      onClick={(event) => onClick(item, event)}
      target={item.openInNewWindow ? '_blank' : undefined}
    />
  );

  const mediaApplications = applicationGroupsList?.[ApplicationsListCategories.Media];
  const programmableMediaApplications = applicationGroupsList?.[ApplicationsListCategories.ProgrammableMedia];

  return applicationListFetchStatus === FetchStatus.Failure ? (
    <ErrorRefresh message={formatMessage(messages.cantAccessYourProducts)} />
  ) : (
    <>
      {mediaApplications?.map(renderApplicationItem)}
      {programmableMediaApplications?.map(renderApplicationItem)}
      {mediaApplications?.find(({ appId }) => appId === AppId.MediaAPI) &&
        isVideoAppSelectorVisible &&
        renderApplicationItem(applicationsInfo[AppId.Video])}
      {userData &&
        [UserRole.MasterAdmin, UserRole.Admin, UserRole.TechnicalAdmin].includes(userData.role) &&
        renderApplicationItem(applicationsInfo[AppId.MediaFlows])}
      {userData &&
        [UserRole.MasterAdmin, UserRole.Admin, UserRole.TechnicalAdmin].includes(userData.role) &&
        renderApplicationItem(applicationsInfo[AppId.Dimensions])}
      {userData &&
        [UserRole.MasterAdmin, UserRole.Admin, UserRole.TechnicalAdmin].includes(userData.role) &&
        renderApplicationItem(applicationsInfo[AppId.FinalTouch])}
      {isExplorerLinkFlag && isAnyTypeOfAdmin(userData) && <ApplicationsExplorerLink />}
    </>
  );
};
